import React, { useMemo, useState } from 'react';
import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CBadge,
} from '@coreui/react';
import _t from 'counterpart';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import CIcon from '@coreui/icons-react';
import { ITransaction, TransactionType, TransactionStatus } from './transactions/types';
import { TransactionStatusBadge } from './transactions/TransactionStatusBadge';
import { formatCurrency, formatDateTime, shortenId } from '../helpers';
import PaginationTable from '../components/PaginationTable';
import PageLayout from '../components/PageLayout';
import Error from '../components/Error';
import { loadTransactions } from '../services/BackendService';
import PlatformBadge from '../components/PlatformBadge';

const WithdrawalsPage = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'processed'>('pending');
  const [pendingPage, setPendingPage] = useState(1);
  const [processedPage, setProcessedPage] = useState(1);
  const limit = 20;

  const history = useHistory();
  const queryClient = useQueryClient();

  // Pending withdrawals query
  const pendingQuery = useQuery(
    ['pending-withdrawals', pendingPage],
    () =>
      loadTransactions(TransactionType.Withdrawal, '', limit, (pendingPage - 1) * limit, 'createdAt|DESC', {
        createdAtRange: undefined,
        amountFrom: undefined,
        amountTo: undefined,
        providers: undefined,
        statuses: [TransactionStatus.Created, TransactionStatus.Pending],
        wallet: undefined,
        discountCodeIds: undefined,
      }),
    { keepPreviousData: true }
  );

  // Processed withdrawals query (successful, canceled, failed)
  const processedQuery = useQuery(
    ['processed-withdrawals', processedPage],
    () =>
      loadTransactions(TransactionType.Withdrawal, '', limit, (processedPage - 1) * limit, 'createdAt|DESC', {
        createdAtRange: undefined,
        amountFrom: undefined,
        amountTo: undefined,
        providers: undefined,
        statuses: [TransactionStatus.Successful, TransactionStatus.Canceled, TransactionStatus.Failed],
        wallet: undefined,
        discountCodeIds: undefined,
      }),
    { keepPreviousData: true }
  );

  const onRowClicked = (transaction: ITransaction) => {
    history.push(`/transactions/${transaction.id}`);
  };

  const openInNewTab = (transaction: ITransaction) => {
    window.open(`/transactions/${transaction.id}`, '_blank');
  };

  const kycBadgeState: any = useMemo(
    () => ({
      APPROVED: {
        name: 'cil-check-circle',
        class: 'text-success',
      },
      REVOKED: {
        name: 'cil-x-circle',
        class: 'text-danger',
      },
      PENDING: {
        name: 'cil-x-circle',
        class: 'text-danger',
      },
    }),
    []
  );

  const pendingTableFields = [
    { key: 'id', label: _t('global.id') },
    { key: 'createdAt', label: _t('global.created-at') },
    { key: 'customer', label: _t('global.customer') },
    { key: 'wallet', label: _t('global.wallet') },
    { key: 'platform', label: _t('customer.wallets.platform') },
    { key: 'kyc', label: 'KYC' },
    { key: 'amount', label: _t('global.amount') },
    { key: 'status', label: _t('global.status') },
  ];

  const processedTableFields = [
    { key: 'id', label: _t('global.id') },
    { key: 'createdAt', label: _t('global.created-at') },
    { key: 'customer', label: _t('global.customer') },
    { key: 'wallet', label: _t('global.wallet') },
    { key: 'platform', label: _t('customer.wallets.platform') },
    { key: 'amount', label: _t('global.amount') },
    { key: 'status', label: _t('global.status') },
  ];

  const pendingScopedSlots = useMemo(
    () => ({
      id: (item: ITransaction) => <td>{shortenId(item.id)}</td>,
      createdAt: (item: ITransaction) => <td className="text-nowrap">{formatDateTime(item.createdAt)}</td>,
      customer: (item: ITransaction) => <td>{item.customer.name}</td>,
      wallet: (item: ITransaction) => <td>{item.wallet}</td>,
      platform: (item: ITransaction) => (
        <td>{item.platform ? <PlatformBadge platform={item.platform} /> : '-'}</td>
      ),
      amount: (item: ITransaction) => <td>{formatCurrency(item.amount, item.currency)}</td>,
      status: (item: ITransaction) => (
        <td>
          <TransactionStatusBadge status={item.status} />
        </td>
      ),
      kyc: (item: ITransaction) => {
        const { kycStatus } = item.customer;
        return (
          <td>
            {kycStatus && <CIcon className={kycBadgeState[kycStatus].class} name={kycBadgeState[kycStatus].name} />}
            {!kycStatus && <CIcon className="danger" name="cil-minus-circle" />}
          </td>
        );
      },
    }),
    [kycBadgeState]
  );

  const processedScopedSlots = useMemo(
    () => ({
      id: (item: ITransaction) => <td>{shortenId(item.id)}</td>,
      createdAt: (item: ITransaction) => <td className="text-nowrap">{formatDateTime(item.createdAt)}</td>,
      customer: (item: ITransaction) => <td>{item.customer.name}</td>,
      wallet: (item: ITransaction) => <td>{item.wallet}</td>,
      platform: (item: ITransaction) => (
        <td>{item.platform ? <PlatformBadge platform={item.platform} /> : '-'}</td>
      ),
      amount: (item: ITransaction) => <td>{formatCurrency(item.amount, item.currency)}</td>,
      status: (item: ITransaction) => (
        <td>
          <TransactionStatusBadge status={item.status} />
        </td>
      ),
    }),
    []
  );

  const pendingCount = pendingQuery.data?.transactions?.length || 0;
  const processedCount = processedQuery.data?.transactions?.length || 0;
  const pendingPages = pendingQuery.data?.pages || 0;
  const processedPages = processedQuery.data?.pages || 0;

  if (pendingQuery.isError || processedQuery.isError) {
    return (
      <Error
        onRetry={() => {
          pendingQuery.refetch();
          processedQuery.refetch();
        }}
      />
    );
  }

  return (
    <PageLayout title={_t('transactions.withdrawals')}>
      <CRow>
        <CCol>
          <CCard>
            <CCardHeader>
              <CNav variant="tabs">
                <CNavItem>
                  <CNavLink
                    active={activeTab === 'pending'}
                    onClick={() => setActiveTab('pending')}
                    style={{ cursor: 'pointer' }}
                  >
                    {_t('withdrawals-table.filters.pending')} <CBadge color="warning">{pendingCount}</CBadge>
                  </CNavLink>
                </CNavItem>
                <CNavItem>
                  <CNavLink
                    active={activeTab === 'processed'}
                    onClick={() => setActiveTab('processed')}
                    style={{ cursor: 'pointer' }}
                  >
                    {_t('withdrawals.processed')} <CBadge color="success">{processedCount}</CBadge>
                  </CNavLink>
                </CNavItem>
              </CNav>
            </CCardHeader>
            <CCardBody>
              <CTabContent>
                <CTabPane active={activeTab === 'pending'}>
                  <PaginationTable
                    data={pendingQuery.data?.transactions || []}
                    tableFields={pendingTableFields}
                    scopedSlots={pendingScopedSlots}
                    pages={pendingPages}
                    activePage={pendingPage}
                    onPageChanged={setPendingPage}
                    loading={pendingQuery.isLoading}
                    pagination
                    clickableRows
                    onRowClicked={onRowClicked}
                    onRowCommandClicked={openInNewTab}
                  />
                </CTabPane>
                <CTabPane active={activeTab === 'processed'}>
                  <PaginationTable
                    data={processedQuery.data?.transactions || []}
                    tableFields={processedTableFields}
                    scopedSlots={processedScopedSlots}
                    pages={processedPages}
                    activePage={processedPage}
                    onPageChanged={setProcessedPage}
                    loading={processedQuery.isLoading}
                    pagination
                    clickableRows
                    onRowClicked={onRowClicked}
                    onRowCommandClicked={openInNewTab}
                  />
                </CTabPane>
              </CTabContent>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </PageLayout>
  );
};

export default WithdrawalsPage;
